"use client"

/**
 * One question, one row — shared by the Add-questions roster (Questions tab) and
 * the import preview inside the import sheet.
 *
 * Stem is primary (what students see); title is secondary under it when both
 * exist. Meta stays one muted middot line. Points sit in a chip on the far
 * right edge. Row actions stay hidden until hover or keyboard focus so a long
 * list has a quiet right edge.
 *
 * When `onOpen` is set, the row opens the question sheet — same job as a pool
 * row click. The stem is a real `<button>` for keyboard (WCAG 2.1.1); pointer
 * clicks on the row body also open, except on the remove control.
 *
 * Not a DS primitive — a local composition for this flow. `DataRowList` owns the
 * list semantics (`<ul>` / `<li>`, virtualization); this owns one row's layout.
 */

import * as React from "react"

import { Badge } from "@/components/ui/badge"
import { TruncatedControl, TruncatedText } from "@/components/data-views"
import { NeedsReviewMark } from "@/components/assessment-structure/needs-review-mark"
import { cn } from "@/lib/utils"
import { ClickableRow } from "@/components/a11y/clickable-row"

/** Matches `DataRowList`'s hub-page padding down to panel / sheet density. */
export const QUESTION_ROW_LIST_CLASSNAME = "-mx-2 gap-0 p-0 lg:px-0"

/**
 * Per-row border rather than `divide-y` on the list: the rule has to survive the
 * virtualizer swapping rows in and out past 100 questions.
 */
export const QUESTION_ROW_DIVIDER_CLASSNAME =
  "border-b border-border last:border-b-0"

export function QuestionRow({
  position,
  label,
  detail,
  meta,
  trailing,
  action,
  actionMode = "inline",
  leading,
  needsReview,
  reviewReasons,
  onOpen,
}: {
  /** 1-based position in the section. Omit in previews, where order is not a fact yet. */
  position?: number
  /** The question's short title (secondary when a stem is also shown). */
  label: string
  /** Question stem / prompt students see — primary line when set. */
  detail?: string
  /** Short facts joined with middots — keep to two or three. */
  meta: (string | undefined)[]
  /** Value chip on the row's right edge, e.g. points. */
  trailing?: React.ReactNode
  /** Row action, revealed on hover / focus. */
  action?: React.ReactNode
  /**
   * `inline` keeps actions in the trailing cluster (still opacity-gated).
   * `overlay` floats them over the row so they never reserve horizontal space.
   */
  actionMode?: "inline" | "overlay"
  /** Leading control (e.g. drag grip) before the position numeral. */
  leading?: React.ReactNode
  /** Amber triangle when the question needs author attention. */
  needsReview?: boolean
  /** Reasons shown in the needs-review tip. */
  reviewReasons?: string[]
  /** Opens the question preview sheet. Omit when the row is display-only. */
  onOpen?: () => void
}) {
  const metaLine = meta.filter(Boolean).join(" · ")
  const stem =
    detail && detail.trim() && detail.trim() !== label.trim()
      ? detail.trim()
      : null
  const primary = stem ?? label.trim()
  const secondary = stem ? label.trim() : null

  const openFromRow = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onOpen) return
    const target = event.target as HTMLElement
    if (
      target.closest(
        "button, a, input, select, textarea, [role='button'], [data-row-control]"
      )
    ) {
      return
    }
    onOpen()
  }

  const actionEl =
    action && actionMode === "inline" ? (
      <span
        data-row-control
        className={cn(
          "opacity-0 transition-opacity",
          "group-hover/question-row:opacity-100 focus-within:opacity-100"
        )}
      >
        {action}
      </span>
    ) : null

  return (
    <ClickableRow
      onActivate={onOpen ? openFromRow : undefined}
      className={cn(
        "group/question-row relative flex w-full items-start gap-2 px-2 py-1",
        onOpen && "cursor-pointer",
        needsReview &&
          "border-l-2 border-l-[var(--chip-destructive)] bg-[var(--status-badge-danger-fill)]",
      )}
    >
      {leading}
      {needsReview ? (
        <NeedsReviewMark
          reasons={reviewReasons ?? []}
          className="mt-0.5"
        />
      ) : null}
      {position !== undefined ? (
        <span
          className="w-4 shrink-0 text-end text-sm leading-5 text-muted-foreground tabular-nums"
          aria-hidden
        >
          {position}
        </span>
      ) : null}

      <div className="min-w-0 flex-1">
        {onOpen ? (
          <TruncatedControl label={primary}>
            {(ref) => (
              <button
                ref={ref as React.RefCallback<HTMLButtonElement>}
                type="button"
                onClick={onOpen}
                className="line-clamp-2 w-full rounded-1 text-start text-sm leading-5 font-medium text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                {primary}
              </button>
            )}
          </TruncatedControl>
        ) : (
          <TruncatedText
            text={primary}
            as="p"
            className="line-clamp-2 text-sm leading-5 font-medium text-foreground whitespace-normal"
          />
        )}
        {secondary ? (
          <TruncatedText
            text={secondary}
            as="p"
            className="mt-0.5 line-clamp-1 text-xs leading-4 text-muted-foreground"
          />
        ) : null}
        {metaLine ? (
          <TruncatedText
            text={metaLine}
            as="p"
            className="mt-0.5 truncate text-xs leading-4 text-muted-foreground"
          />
        ) : null}
      </div>

      {trailing || actionEl ? (
        <div className="ms-auto flex shrink-0 items-start gap-1">
          {trailing ? (
            <Badge variant="secondary" className="tabular-nums">
              {trailing}
            </Badge>
          ) : null}
          {actionEl}
        </div>
      ) : null}

      {action && actionMode === "overlay" ? (
        <span
          data-row-control
          className={cn(
            "absolute end-1 top-1/2 z-10 flex -translate-y-1/2 items-center gap-0.5",
            "rounded-1 bg-background/95 p-0.5 shadow-sm ring-1 ring-border/70",
            "opacity-0 transition-opacity",
            "group-hover/question-row:opacity-100 focus-within:opacity-100"
          )}
        >
          {action}
        </span>
      ) : null}
    </ClickableRow>
  )
}
